import { useMemo, useState, useEffect } from 'react';
import CubMascot from '../components/CubMascot.jsx';

/* Fisher-Yates, run once per page via useMemo so items do not
   jump around every time React re-renders. */
function shuffle(list) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function CubTip({ children }) {
  return (
    <div className="cub-tip">
      <CubMascot avatar="cub-sun" mood="star" size={64} />
      <div>
        <strong>Cub says…</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

/* ============================================================
   intro
   ============================================================ */
export function IntroPage({ page }) {
  return (
    <div className="slide slide-intro tc">
      <CubMascot avatar="cub-grape" mood={page.mascot ?? 'wave'} size={168} className="anim-float" />
      <h1>{page.title}</h1>
      <p className="slide-lede">{page.text}</p>
      {page.chips && (
        <div className="row center wrap gap-sm mt-md">
          {page.chips.map((c) => (
            <span key={c} className="pill pill-tone t-grape">{c}</span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   teach
   ============================================================ */
/**
 * A picture on a teaching page.
 *
 * It hides itself if the file will not load, rather than leaving a
 * broken-image icon in the middle of a lesson: the words around it
 * have to stand on their own anyway, and a child seeing a torn page
 * assumes they have broken something.
 */
function SlideFigure({ src, alt, caption }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;

  return (
    <figure className="slide-figure">
      <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

/**
 * A short chain of stages - "press power, firmware, POST, ..." - drawn
 * as chips with arrows between them.
 *
 * The arrows are separate spans rather than CSS pseudo-elements so the
 * whole thing wraps properly on a phone: a chain of six reads as two
 * rows of three rather than scrolling sideways off the page.
 */
function SlideFlow({ title, steps }) {
  return (
    <div className="slide-flow">
      {title && <strong className="slide-flow-title">{title}</strong>}
      <ol>
        {steps.map((s, i) => (
          <li key={s}>
            <span className="slide-flow-step">{s}</span>
            {i < steps.length - 1 && (
              <span className="slide-flow-arrow" aria-hidden="true">→</span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

/** One numbered step of a process, with its own optional analogy. */
function SlideStep({ step, n }) {
  return (
    <section className="slide-step">
      <header>
        <span className="slide-step-n" aria-hidden="true">{n}</span>
        <h3>
          {step.icon && <span aria-hidden="true">{step.icon} </span>}
          {step.title}
        </h3>
      </header>

      {step.body?.map((p, i) => <p key={i}>{p}</p>)}

      {step.flow && <SlideFlow steps={step.flow} />}

      {step.note && (
        <div className="slide-step-note">
          <strong>{step.note.title}</strong>
          <ul>
            {step.note.points.map((p) => <li key={p}>{p}</li>)}
          </ul>
        </div>
      )}

      {step.example && (
        <div className="slide-step-example">
          <strong>Picture it</strong>
          {step.example.intro && <p>{step.example.intro}</p>}
          {step.example.points && (
            <ul>
              {step.example.points.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
          {step.example.close && <p>{step.example.close}</p>}
        </div>
      )}
    </section>
  );
}

export function TeachPage({ page }) {
  return (
    <div className="slide">
      <h2>{page.title}</h2>
      {page.body?.map((p, i) => (
        <p key={i} className="slide-body">{p}</p>
      ))}

      {page.figure && <SlideFigure {...page.figure} />}

      {page.flow && <SlideFlow {...page.flow} />}

      {page.steps?.map((s, i) => <SlideStep key={s.title} step={s} n={i + 1} />)}

      {page.bullets && (
        <div className="bullet-grid">
          {page.bullets.map((b, i) => (
            <div key={i} className="bullet anim-up" style={{ animationDelay: `${i * 60}ms` }}>
              <span className="bullet-icon" aria-hidden="true">{b.icon}</span>
              <div>
                <strong>{b.title}</strong>
                <p className="small muted">{b.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {page.tip && <CubTip>{page.tip}</CubTip>}

      {/* A teach page can point somewhere too - the Software Basics
          explainers hang off one. Same block a practical uses, so a
          learner meets one kind of button, not two. */}
      {page.site && (
        <div className="lab-launch">
          <div>
            <strong>{page.site.title}</strong>
            <p className="small muted">{page.site.note}</p>
          </div>
          <a
            href={page.site.href}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-site"
          >
            🗔 {page.site.label ?? 'Open'}
          </a>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   cards
   ============================================================ */
export function CardsPage({ page }) {
  return (
    <div className="slide">
      <h2>{page.title}</h2>
      {page.intro && <p className="slide-body">{page.intro}</p>}

      <div className="concept-grid">
        {page.cards.map((c, i) => (
          <div
            key={c.title}
            className={`concept t-${c.color}`}
            style={{ animationDelay: `${i * 70}ms` }}
          >
            <span className="concept-icon" aria-hidden="true">{c.icon}</span>
            {c.tag && <span className="concept-tag">{c.tag}</span>}
            <h3>{c.title}</h3>
            <p className="small">{c.text}</p>
          </div>
        ))}
      </div>

      {page.tip && <CubTip>{page.tip}</CubTip>}
    </div>
  );
}

/* ============================================================
   choice - a story with "what would you do?"
   ============================================================ */
export function ChoicePage({ page, onComplete }) {
  const [picked, setPicked] = useState(null);
  const [tried, setTried] = useState([]);

  const chosen = page.options.find((o) => o.id === picked);

  const pick = (option) => {
    setPicked(option.id);
    setTried((t) => (t.includes(option.id) ? t : [...t, option.id]));
    if (option.correct) onComplete();
  };

  return (
    <div className="slide">
      <h2>{page.title}</h2>

      <div className="scenario">
        <span className="scenario-icon" aria-hidden="true">{page.scenario.icon}</span>
        <div>
          <span className="scenario-from">{page.scenario.from}</span>
          <p className="scenario-text">{page.scenario.text}</p>
        </div>
      </div>

      <h3 className="mt-md">{page.question}</h3>

      <div className="option-list">
        {page.options.map((o) => {
          const isPicked = picked === o.id;
          const wasTried = tried.includes(o.id);
          const state = isPicked ? (o.correct ? 'right' : 'wrong') : wasTried ? 'tried' : '';
          return (
            <button
              key={o.id}
              type="button"
              className={`option ${state}`}
              onClick={() => pick(o)}
              disabled={chosen?.correct}
            >
              <span className="option-mark" aria-hidden="true">
                {isPicked ? (o.correct ? '✓' : '✗') : ''}
              </span>
              <span>{o.label}</span>
            </button>
          );
        })}
      </div>

      {chosen && (
        <div className={`feedback ${chosen.correct ? 'good' : 'bad'} anim-pop`}>
          <CubMascot avatar={chosen.correct ? 'cub-mint' : 'cub-coral'}
            mood={chosen.correct ? 'cheer' : 'sad'} size={72} />
          <div>
            <strong>{chosen.correct ? 'Yes! Great thinking.' : 'Not quite — have another go.'}</strong>
            <p>{chosen.feedback}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   sort - tap an item, then tap a bucket
   ============================================================ */
export function SortPage({ page, onComplete }) {
  const items = useMemo(() => shuffle(page.items), [page.items]);
  const [placed, setPlaced] = useState({});
  const [selected, setSelected] = useState(null);
  const [wrong, setWrong] = useState(null);

  const remaining = items.filter((i) => !placed[i.id]);
  const solved = remaining.length === 0;

  useEffect(() => {
    if (solved) onComplete();
  }, [solved, onComplete]);

  const dropInto = (bucketId) => {
    if (!selected) return;
    const item = items.find((i) => i.id === selected);
    if (item.bucket === bucketId) {
      setPlaced((p) => ({ ...p, [item.id]: bucketId }));
      setSelected(null);
      setWrong(null);
    } else {
      setWrong(bucketId);
      setTimeout(() => setWrong(null), 500);
    }
  };

  return (
    <div className="slide">
      <h2>{page.title}</h2>
      <p className="slide-body">{page.instruction}</p>

      <div className="sort-progress">
        {items.length - remaining.length} of {items.length} sorted
      </div>

      <div className="sort-pool">
        {remaining.length === 0 ? (
          <div className="sort-done">🎉 All sorted! Nice work.</div>
        ) : (
          remaining.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`sort-item ${selected === item.id ? 'is-selected' : ''}`}
              onClick={() => setSelected(selected === item.id ? null : item.id)}
              aria-pressed={selected === item.id}
            >
              <span aria-hidden="true">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))
        )}
      </div>

      <div className="bucket-row">
        {page.buckets.map((b) => {
          const contents = items.filter((i) => placed[i.id] === b.id);
          return (
            <div
              key={b.id}
              className={`bucket t-${b.color} ${selected ? 'is-ready' : ''} ${wrong === b.id ? 'anim-shake is-wrong' : ''}`}
            >
              <button
                type="button"
                className="bucket-label"
                onClick={() => dropInto(b.id)}
                disabled={!selected}
              >
                {b.label}
              </button>
              <div className="bucket-items">
                {contents.map((c) => (
                  <span key={c.id} className="bucket-chip anim-pop">
                    <span aria-hidden="true">{c.icon}</span> {c.label}
                  </span>
                ))}
                {contents.length === 0 && <span className="bucket-hint">Drop items here</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   spot - tap the suspicious lines in a fake message
   ============================================================ */
export function SpotPage({ page, onComplete }) {
  const [tapped, setTapped] = useState({});
  const isChat = page.variant === 'chat';
  const traps = page.lines.filter((l) => l.suspicious);
  const found = traps.filter((l) => tapped[l.id]).length;
  const allFound = found === traps.length;

  useEffect(() => {
    if (allFound) onComplete();
  }, [allFound, onComplete]);

  const last = page.lines.find((l) => tapped[l.id] === 'latest');

  const tap = (line) => {
    setTapped((t) => {
      const next = {};
      for (const [k, v] of Object.entries(t)) next[k] = v === 'latest' ? true : v;
      next[line.id] = 'latest';
      return next;
    });
  };

  return (
    <div className="slide">
      <h2>{page.title}</h2>
      <p className="slide-body">{page.instruction}</p>

      <div className="spot-counter">
        🔎 Red flags found: <strong>{found} / {traps.length}</strong>
      </div>

      <div className={`fake-message ${isChat ? 'is-chat' : ''}`}>
        <div className="fake-head">
          <span className="fake-icon" aria-hidden="true">{page.message.icon}</span>
          <div>
            <div className="fake-from">{page.message.from}</div>
            <div className="fake-subject">{page.message.subject}</div>
          </div>
        </div>

        <div className="fake-body">
          {page.lines.map((line) => {
            const state = tapped[line.id];
            const cls = state ? (line.suspicious ? 'is-flag' : 'is-safe') : '';
            // In chat mode "me" bubbles sit on the right, like a real app.
            const side = isChat ? (line.who === 'me' ? 'from-me' : 'from-them') : '';
            return (
              <button
                key={line.id}
                type="button"
                className={`fake-line ${cls} ${side}`}
                onClick={() => tap(line)}
              >
                {state && (
                  <span className="fake-mark" aria-hidden="true">{line.suspicious ? '🚩' : '👍'}</span>
                )}
                <span>{line.text}</span>
              </button>
            );
          })}
        </div>
      </div>

      {last && (
        <div className={`feedback ${last.suspicious ? 'good' : 'neutral'} anim-pop`}>
          <CubMascot avatar={last.suspicious ? 'cub-mint' : 'cub-sky'}
            mood={last.suspicious ? 'cheer' : 'think'} size={72} />
          <div>
            <strong>{last.suspicious ? 'Red flag spotted! 🚩' : 'This line is actually okay.'}</strong>
            <p>{last.why}</p>
          </div>
        </div>
      )}

      {allFound && page.debrief && (
        <div className="feedback good anim-pop">
          <span className="feedback-emoji" aria-hidden="true">🎉</span>
          <div>
            <strong>You found them all!</strong>
            <p>{page.debrief}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   match - tap a term, then tap its meaning
   ============================================================ */
export function MatchPage({ page, onComplete }) {
  const meanings = useMemo(() => shuffle(page.pairs), [page.pairs]);
  const [matched, setMatched] = useState({});
  const [selected, setSelected] = useState(null);
  const [wrong, setWrong] = useState(null);

  const done = Object.keys(matched).length === page.pairs.length;

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const chooseMeaning = (pair) => {
    if (!selected) return;
    if (selected === pair.id) {
      setMatched((m) => ({ ...m, [pair.id]: true }));
      setSelected(null);
      setWrong(null);
    } else {
      setWrong(pair.id);
      setTimeout(() => setWrong(null), 500);
    }
  };

  return (
    <div className="slide">
      <h2>{page.title}</h2>
      <p className="slide-body">{page.instruction}</p>

      <div className="match-progress">
        {Object.keys(matched).length} of {page.pairs.length} matched
      </div>

      <div className="match-grid">
        <div className="match-col">
          {page.pairs.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`match-term ${matched[p.id] ? 'is-done' : ''} ${selected === p.id ? 'is-selected' : ''}`}
              onClick={() => !matched[p.id] && setSelected(selected === p.id ? null : p.id)}
              disabled={matched[p.id]}
              aria-pressed={selected === p.id}
            >
              <span aria-hidden="true">{p.icon}</span>
              <span>{p.term}</span>
              {matched[p.id] && <span className="match-tick" aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>

        <div className="match-col">
          {meanings.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`match-meaning ${matched[p.id] ? 'is-done' : ''} ${wrong === p.id ? 'anim-shake is-wrong' : ''}`}
              onClick={() => !matched[p.id] && chooseMeaning(p)}
              disabled={matched[p.id] || !selected}
            >
              {p.meaning}
              {matched[p.id] && <span className="match-tick" aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      </div>

      {done && (
        <div className="feedback good anim-pop">
          <span className="feedback-emoji" aria-hidden="true">🎉</span>
          <div>
            <strong>All matched!</strong>
            <p>You know exactly who does what on the cyber squad.</p>
          </div>
        </div>
      )}
    </div>
  );
}
